import { useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  UserCog,
  Briefcase,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DAY_LABELS = ["Ontem", "Hoje", "Amanhã"];

const formatDate = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
};

const PERSONAS = [
  { id: "secretaria", label: "Secretária", icon: UserCog, desc: "Foco operacional" },
  { id: "socio", label: "Sócio", icon: Briefcase, desc: "Foco financeiro" },
];

export const Header = ({ persona, setPersona, canSocio = true }) => {
  const [dayOffset, setDayOffset] = useState(0);
  const personas = canSocio ? PERSONAS : PERSONAS.filter((p) => p.id !== "socio");
  const activePersona = personas.find((p) => p.id === persona) || personas[0];
  const clampedIndex = Math.max(0, Math.min(2, dayOffset + 1));

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-5 py-3.5 lg:flex-row lg:items-center lg:justify-between md:px-8">
        {/* Left: Brand */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-11 w-auto" />
            <span
              data-testid="version-badge"
              className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500"
            >
              v1.0 MVP
            </span>
          </div>
        </div>

        {/* Center: Date switcher */}
        <div className="flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white p-1 shadow-sm">
          <button
            data-testid="date-prev-btn"
            onClick={() => setDayOffset((d) => Math.max(-1, d - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-900"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex flex-col items-center px-3 min-w-[190px]">
            <span className="text-xs font-semibold text-blue-600">
              {DAY_LABELS[clampedIndex]}
            </span>
            <span className="text-[13px] font-medium capitalize text-slate-700">
              {formatDate(dayOffset)}
            </span>
          </div>
          <button
            data-testid="date-next-btn"
            onClick={() => setDayOffset((d) => Math.min(1, d + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-900"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Right: AI badge + persona */}
        <div className="flex items-center gap-3">
          <div
            data-testid="copilot-status"
            className="flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700">
              Copiloto WhatsApp Ativo
            </span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="persona-selector"
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:bg-slate-50"
              >
                <activePersona.icon className="h-4 w-4 text-blue-600" />
                {activePersona.label}
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {personas.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  data-testid={`persona-option-${p.id}`}
                  onClick={() => setPersona(p.id)}
                  className="cursor-pointer gap-2.5 py-2"
                >
                  <p.icon className="h-4 w-4 text-blue-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-800">{p.label}</span>
                    <span className="text-xs text-slate-400">{p.desc}</span>
                  </div>
                  {persona === p.id && (
                    <motion.span
                      layoutId="persona-check"
                      className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-600"
                    />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
